import { useMemo } from "react";
import { Filter } from "lucide-react";
import { cn } from "@/utils/cn";
import { resolveAtivoTipo, type ContratoDto, type FiltroTipoAtivo, type MaquinarioDto } from "@/services/locacoes";
import { locacoesFilterChipClass } from "./locacoesUtils";

const fieldSelectClass =
  "h-10 w-full cursor-pointer rounded-lg border border-slate-200 bg-slate-50 px-2.5 text-xs text-slate-800 outline-none transition-colors focus:border-brand-600 focus:ring-2 focus:ring-brand-600 sm:text-sm";

export interface LocacoesScopeValue {
  clienteSiengeId: string;
  contratoId: string;
  maquinaId: string;
}

export const EMPTY_LOCACOES_SCOPE: LocacoesScopeValue = {
  clienteSiengeId: "",
  contratoId: "",
  maquinaId: "",
};

interface Props {
  contratos: ContratoDto[];
  maquinarios: MaquinarioDto[];
  filtroTipo?: FiltroTipoAtivo;
  scope: LocacoesScopeValue;
  onChange: (scope: LocacoesScopeValue) => void;
  variant?: "chip" | "fields";
}

const selectClass =
  "h-8 max-w-[10rem] min-w-[7rem] cursor-pointer rounded-lg border-0 bg-transparent pr-6 text-xs text-slate-700 outline-none focus:ring-0";

function uniqueClientes(contratos: ContratoDto[]): Array<{ id: string; label: string }> {
  const map = new Map<string, string>();
  for (const c of contratos) {
    if (!c.clienteSiengeId) continue;
    if (!map.has(c.clienteSiengeId)) {
      map.set(c.clienteSiengeId, c.clienteSiengeNome || c.clienteSiengeId);
    }
  }
  return [...map.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

function sortContratosPorCodigo(a: ContratoDto, b: ContratoDto): number {
  const anoDiff = (b.codigoAno ?? 0) - (a.codigoAno ?? 0);
  if (anoDiff !== 0) return anoDiff;
  return (b.codigoSequencial ?? 0) - (a.codigoSequencial ?? 0);
}

function pickBestContrato(candidates: ContratoDto[]): ContratoDto | undefined {
  if (candidates.length === 0) return undefined;
  if (candidates.length === 1) return candidates[0];
  const active = candidates.filter((c) => c.status === "ativo");
  const pool = active.length > 0 ? active : candidates;
  return [...pool].sort(sortContratosPorCodigo)[0];
}

function contratosComMaquina(contratos: ContratoDto[], maquinaId: string): ContratoDto[] {
  return contratos.filter((c) => c.maquinaIds.includes(maquinaId));
}

/** Mantém cliente, contrato e máquina coerentes entre si. */
export function normalizeLocacoesScope(
  contratos: ContratoDto[],
  scope: LocacoesScopeValue,
): LocacoesScopeValue {
  let { clienteSiengeId, contratoId, maquinaId } = scope;

  if (clienteSiengeId && !contratos.some((c) => c.clienteSiengeId === clienteSiengeId)) {
    clienteSiengeId = "";
    contratoId = "";
    maquinaId = "";
  }

  if (contratoId) {
    const contrato = contratos.find((c) => c.id === contratoId);
    if (!contrato) {
      contratoId = "";
    } else {
      clienteSiengeId = contrato.clienteSiengeId || clienteSiengeId;
      if (maquinaId && !contrato.maquinaIds.includes(maquinaId)) {
        maquinaId = "";
      }
    }
  }

  if (clienteSiengeId && contratoId) {
    const contrato = contratos.find((c) => c.id === contratoId);
    if (!contrato || contrato.clienteSiengeId !== clienteSiengeId) {
      contratoId = "";
    }
  }

  if (maquinaId) {
    let candidates = contratosComMaquina(contratos, maquinaId);
    if (clienteSiengeId) {
      candidates = candidates.filter((c) => c.clienteSiengeId === clienteSiengeId);
    }
    if (contratoId) {
      if (!candidates.some((c) => c.id === contratoId)) {
        const best = pickBestContrato(candidates);
        contratoId = best?.id ?? "";
        clienteSiengeId = best?.clienteSiengeId ?? clienteSiengeId;
      }
    } else if (candidates.length > 0) {
      const best = pickBestContrato(candidates);
      contratoId = best?.id ?? "";
      clienteSiengeId = best?.clienteSiengeId ?? clienteSiengeId;
    } else {
      maquinaId = "";
    }
  }

  if (contratoId) {
    const contrato = contratos.find((c) => c.id === contratoId);
    if (contrato) {
      clienteSiengeId = contrato.clienteSiengeId || clienteSiengeId;
    }
  }

  return { clienteSiengeId, contratoId, maquinaId };
}

function ScopeFieldSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  disabled?: boolean;
  className?: string;
}) {
  const id = `loc-scope-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className={className ?? "min-w-[140px] max-w-[200px] flex-shrink-0"}>
      <label htmlFor={id} className="mb-1.5 block truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={fieldSelectClass}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function LocacoesScopeFilter({
  contratos,
  maquinarios,
  filtroTipo = "todos",
  scope,
  onChange,
  variant = "chip",
}: Props) {
  const emit = (next: LocacoesScopeValue) => {
    onChange(normalizeLocacoesScope(contratos, next));
  };

  // Filtros posteriores nunca restringem as opções dos filtros anteriores.
  const clientes = useMemo(() => uniqueClientes(contratos), [contratos]);

  const contratosFiltrados = useMemo(() => {
    let list = contratos;
    if (scope.clienteSiengeId) {
      list = list.filter((c) => c.clienteSiengeId === scope.clienteSiengeId);
    }
    return [...list].sort(sortContratosPorCodigo);
  }, [contratos, scope.clienteSiengeId]);

  const maquinasDisponiveis = useMemo(() => {
    let contratosBase = contratos;
    if (scope.clienteSiengeId) {
      contratosBase = contratosBase.filter((c) => c.clienteSiengeId === scope.clienteSiengeId);
    }
    if (scope.contratoId) {
      contratosBase = contratosBase.filter((c) => c.id === scope.contratoId);
    }
    const ids = new Set<string>();
    for (const c of contratosBase) {
      for (const id of c.maquinaIds) ids.add(id);
    }
    const byId = new Map(maquinarios.map((m) => [m.id, m]));
    return [...ids]
      .map((id) => byId.get(id))
      .filter((m): m is MaquinarioDto => Boolean(m))
      .filter((m) => filtroTipo === "todos" || resolveAtivoTipo(m) === filtroTipo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [contratos, maquinarios, filtroTipo, scope.clienteSiengeId, scope.contratoId]);

  const ativosPlaceholder =
    filtroTipo === "maquinario"
      ? "Todas as máquinas"
      : filtroTipo === "equipamento"
        ? "Todos os equipamentos"
        : "Todas as máquinas/equipamentos";

  if (variant === "fields") {
    return (
      <>
        <ScopeFieldSelect
          label="Cliente"
          value={scope.clienteSiengeId}
          onChange={(clienteSiengeId) =>
            emit({
              clienteSiengeId,
              contratoId: "",
              maquinaId: "",
            })
          }
          placeholder="Todos os clientes"
          options={clientes.map((c) => ({ value: c.id, label: c.label }))}
        />
        <ScopeFieldSelect
          label="Contrato"
          value={scope.contratoId}
          onChange={(contratoId) => {
            if (!contratoId) {
              emit({
                clienteSiengeId: scope.clienteSiengeId,
                contratoId: "",
                maquinaId: "",
              });
              return;
            }
            const contrato = contratos.find((c) => c.id === contratoId);
            emit({
              clienteSiengeId: contrato?.clienteSiengeId ?? scope.clienteSiengeId,
              contratoId,
              maquinaId: "",
            });
          }}
          placeholder="Todos os contratos"
          options={contratosFiltrados.map((c) => ({
            value: c.id,
            label: `${c.codigoContrato}${scope.clienteSiengeId ? "" : ` — ${c.clienteSiengeNome}`}`,
          }))}
        />
        <ScopeFieldSelect
          label="Máquina/equipamento"
          value={scope.maquinaId}
          onChange={(maquinaId) =>
            emit({
              clienteSiengeId: scope.clienteSiengeId,
              contratoId: scope.contratoId,
              maquinaId,
            })
          }
          placeholder={ativosPlaceholder}
          disabled={maquinasDisponiveis.length === 0}
          options={maquinasDisponiveis.map((m) => ({ value: m.id, label: m.nome }))}
          className="min-w-[140px] max-w-[220px] flex-shrink-0"
        />
      </>
    );
  }

  return (
    <div className={cn(locacoesFilterChipClass, "max-w-full gap-1 px-2.5")}>
      <Filter className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
      <select
        value={scope.clienteSiengeId}
        onChange={(e) =>
          emit({
            clienteSiengeId: e.target.value,
            contratoId: "",
            maquinaId: "",
          })
        }
        className={selectClass}
        aria-label="Filtrar por cliente"
      >
        <option value="">Todos os clientes</option>
        {clientes.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
      <select
        value={scope.contratoId}
        onChange={(e) => {
          const contratoId = e.target.value;
          if (!contratoId) {
            emit({
              clienteSiengeId: scope.clienteSiengeId,
              contratoId: "",
              maquinaId: "",
            });
            return;
          }
          const contrato = contratos.find((c) => c.id === contratoId);
          emit({
            clienteSiengeId: contrato?.clienteSiengeId ?? scope.clienteSiengeId,
            contratoId,
            maquinaId: "",
          });
        }}
        className={selectClass}
        aria-label="Filtrar por contrato"
      >
        <option value="">Todos os contratos</option>
        {contratosFiltrados.map((c) => (
          <option key={c.id} value={c.id}>
            {c.codigoContrato}
            {scope.clienteSiengeId ? "" : ` — ${c.clienteSiengeNome}`}
          </option>
        ))}
      </select>
      <select
        value={scope.maquinaId}
        onChange={(e) =>
          emit({
            clienteSiengeId: scope.clienteSiengeId,
            contratoId: scope.contratoId,
            maquinaId: e.target.value,
          })
        }
        className={selectClass}
        aria-label="Filtrar por máquina ou equipamento"
        disabled={maquinasDisponiveis.length === 0}
      >
        <option value="">{ativosPlaceholder}</option>
        {maquinasDisponiveis.map((m) => (
          <option key={m.id} value={m.id}>
            {m.nome}
          </option>
        ))}
      </select>
    </div>
  );
}
